
import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import { RUNNITS_OWNER_SELECTION, TRAINING_QUALITY_CARDS } from '@/views/Runnits/constants';
import { setRunnitsTeamAccessLevel } from '@/utils';
import {
	RUNNITS_ACCESS_LEVEL,
	RunnitsAccessLevel,
	RUNNIT_NODE_DEF_TAG_TYPE,
	RunnitNodeDefTag,
	Model,
} from '@run-diffusion/shared';
import { ModelCRUDMixin } from '@/mixins/ModelCRUDMixin';
import { RunnitsCRUDMixin } from '@/mixins';
import ModelSelectCard from '@/views/Runnits/pages/models/ModelSelectCard.vue';
import ModelToolUseDialog from '@/views/Runnits/pages/models/ModelToolUseDialog.vue';
import { ROUTER } from '@/router/constants';
import ModelTagTypeTabs from './ModelTagTypeTabs.vue';
import { fuzzyFilter } from '@/utils/fuzzySearch';
import TrainerUploadEmptyState from '@/views/Runnits/pages/models/TrainerUploadEmptyState.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import { UploadRecord } from '@/store/ModelUploadStore';
import TrainOnboardingTopCard from './TrainOnboardingTopCard.vue';
import {
	getTrainingOnboardingDismissed,
	setTrainingOnboardingDismissed,
	removeTrainingOnboardingDismissed,
} from '@/utils/localStorage';
import { TRAINING_ONBOARDING_DATA } from './constants';
import { TeamUserMixin } from '@/mixins';
import _isEmpty from 'lodash/isEmpty';

export default Vue.extend({
	name: 'RunnitModelsListPage',
	metaInfo: {
		title: 'List',
	},
	mixins: [
		RunnitsCRUDMixin,
		ModelCRUDMixin,
		TeamUserMixin,
	],
	computed: {
		...mapState([
			'user',
			'team',
			'modelsState',
			'runnitState',
			'loadingModels',
			'publicRunnitNodeDefTagsMap',
		]),
		...mapGetters([
			'canDownloadModels',
		]),
		...mapGetters('modelUpload', [
			'getUploading',
			'allUploads'
		]),
		uploadingModelsForEnvironment () {
			return this.getUploading.filter((upload: UploadRecord) =>
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
					? upload.model.teamId === this.team.id && upload.model.accessLevel === this.runnitState.runnitsAccessLevel
					: !upload.model.teamId
			);
		},
		filteredModels () {
			return this.modelsState.models.filter((model: Model) => {
				// Filter by selected type
				if (this.selectedTrainingTypeTagId !== 'ALL') {
					if (_isEmpty(model.tags) || !model.tags[this.selectedTrainingTypeTagId]) {
						return false;
					}
				}

				return true;
			}).filter((model: Model) => {
				if (!this.modelSearchValue) return true;
				return fuzzyFilter(
					[model],
					this.modelSearchValue,
					[
						{ name: 'name', weight: 0.7 },
						{ name: 'description', weight: 0.3 }
					]
				).length > 0;
			});
		},
		noModelsYet (): boolean {
			return !!(
				!this.filteredModels.length &&
				!this.uploadingModelsForEnvironment.length
			);
		},
	},
	components: {
		LoadingState,
		TrainerUploadEmptyState,
		ModelToolUseDialog,
		ModelSelectCard,
		ModelTagTypeTabs,
		TrainOnboardingTopCard,
	},
	watch: {
		isModelToolUseDialogOpen (newVal: boolean) {
			if (!newVal) {
				this.modelSelected = null;
			}
		},
	},
	data () {
		return {
			RUNNITS_ACCESS_LEVEL,
			RUNNITS_OWNER_SELECTION,
			modelList: [],
			isModelToolUseDialogOpen: false,
			modelSelected: null,
			selectedTrainingTypeTagId: 'ALL',
			modelSearchValue: '',
			downloadingModelId: null,
			trainingOnboardingCardDismissed: getTrainingOnboardingDismissed(),
			trainingOnboardingData: TRAINING_ONBOARDING_DATA,
		};
	},
	methods: {
		...mapActions([
			'bindModels',
			'updateSnackbar',
			'updateRunnitState',
			'setUpsellDialog',
		]),
		...mapActions('modelUpload', [
			'uploadModel',
		]),
		onModelsAccessLevelChange (accessLevel: RunnitsAccessLevel) {
			setRunnitsTeamAccessLevel(accessLevel);
			this.updateRunnitState({
				runnitsAccessLevel: accessLevel,
			});
		},
		onModelSelect (model: Model) {
			this.modelSelected = model;
			this.isModelToolUseDialogOpen = true;
		},
		async goToTrainModel () {
			await this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_TRAIN_MODEL_OPTIONS });
		},
		onModelTypeSelect (tagId: string) {
			this.selectedTrainingTypeTagId = tagId;
		},
		handleSearchInput (searchValue: string) {
			this.modelSearchValue = searchValue;
		},
		getModelEstimatedTrainTime (model: Model) {
			const tags: RunnitNodeDefTag[] = Object.keys((model.tags || {})).map((tagId: string) => this.publicRunnitNodeDefTagsMap[tagId]);
			const qualityTag: RunnitNodeDefTag = tags.find((tag: RunnitNodeDefTag) => tag.type === RUNNIT_NODE_DEF_TAG_TYPE.TRAINING_QUALITY) || null;
			return (qualityTag && TRAINING_QUALITY_CARDS[qualityTag.id]?.timeEstimate) || 'several';
		},
		handleDownloadStatusChange ({ modelId, isDownloading }) {
			this.downloadingModelId = isDownloading ? modelId : null;
		},
		toggleTrainingOnboardingCard (nodeDefId: string) {
			this.trainingOnboardingCardDismissed = !this.trainingOnboardingCardDismissed;
			if (this.trainingOnboardingCardDismissed) {
				setTrainingOnboardingDismissed();
			}
		},
		showTrainingOnboardingCard () {
			this.trainingOnboardingCardDismissed = false;
			removeTrainingOnboardingDismissed();
		},
	}
});
